import { useCallback, useMemo, useRef, useState } from 'react'
import { nanoid } from 'nanoid'
import { toastStore } from 'shared/ui'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { useEntitiesContext } from 'entities/EntitiesExport'
import type { Contact } from 'entities/Contacts/model/Contact'
import type { BlockContactModalCallbacks } from 'entities/Contacts/store/contacts'
import { BlockContactModalActions } from './BlockContactModalActions'
import { BlockContactModalContent } from './BlockContactModalContent'

export type blockContactModalOptions = BlockContactModalCallbacks

export type HandleBlockOptions = {
  reportSpam?: boolean
}

type PendingBlockOperation = {
  timeoutId: ReturnType<typeof setTimeout>
  contactId: number
  undoRequested: boolean
  unblockedInCallback: boolean
}

const pendingBlockOperations = new Map<string, PendingBlockOperation>()

export type UseBlockContactModalReturn = {
  handleBlock: (options?: HandleBlockOptions) => void
}

export const useBlockContactModal = (
  contact: Contact | null,
  options: blockContactModalOptions = {}
): UseBlockContactModalReturn => {
  const { contactsStore, eventLogStore, organizationStore } = useEntitiesContext()
  const [modalId] = useState(() => nanoid())
  const reportSpamRef = useRef<boolean>(true)

  const mergedOptions = useMemo(
    () => ({ ...contactsStore.getBlockContactModalCallbacks(), ...options }),
    [contactsStore, options]
  )

  const handleUnlink = useCallback(() => {
    if (!contact) return

    modalStore.closeModal(modalId)
    mergedOptions.resetActiveAndCloseDetailsIsMobile?.()

    const toastId = nanoid()
    const contactId = contact.id
    const reportSpam = reportSpamRef.current

    const timeoutId = setTimeout(async () => {
      const data = pendingBlockOperations.get(toastId)
      if (!data) return

      try {
        await contactsStore.blockContactById(contactId)

        if (data.undoRequested) {
          await contactsStore.unblockContactById(contactId)
          data.unblockedInCallback = true
        } else {
          if (reportSpam) {
            eventLogStore.logEvent(
              'Spam Call Reported',
              { event_id: 'call_marked_as_spam' },
              {
                groupId: organizationStore.id,
              }
            )
          }
        }
      } finally {
        if (data.undoRequested && !data.unblockedInCallback) {
          await contactsStore.unblockContactById(contactId)
        }
        pendingBlockOperations.delete(toastId)
        toastStore.remove(toastId)
      }
    }, 5000)

    pendingBlockOperations.set(toastId, {
      timeoutId,
      contactId,
      undoRequested: false,
      unblockedInCallback: false,
    })

    toastStore.add({
      id: toastId,
      title: reportSpam ? 'Number blocked and marked as spam' : 'Number blocked',
      type: 'success',
      action: {
        text: 'Undo',
        onAction: () => {
          const data = pendingBlockOperations.get(toastId)
          if (data) {
            clearTimeout(data.timeoutId)
            data.undoRequested = true
            pendingBlockOperations.delete(toastId)
          }
          toastStore.remove(toastId)
        },
      },
    })
  }, [
    contact,
    modalId,
    contactsStore,
    eventLogStore,
    organizationStore,
    mergedOptions.resetActiveAndCloseDetailsIsMobile,
  ])

  const handleBlock = useCallback(
    (options?: HandleBlockOptions) => {
      if (!contact) return

      reportSpamRef.current = options?.reportSpam !== false

      if (contact.is_blocked) {
        contactsStore.unblockContactById(contact.id)
        return
      }

      requestAnimationFrame(() => {
        modalStore.addModal({
          id: modalId,
          showCloseButton: false,
          showCloseIcon: false,
          showHeader: false,
          width: 280,
          ModalContentProps: {
            contact,
            onConfirm: handleUnlink,
            onCancel: () => modalStore.closeModal(modalId),
          },
          ModalContent: BlockContactModalContent,
          ModalActions: BlockContactModalActions,
        })
      })
    },
    [contact, modalId, contactsStore, handleUnlink]
  )

  return { handleBlock }
}
